import React from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Grid,
  Typography,
  useMediaQuery,
  useTheme,
} from '@material-ui/core';
import { makeStyles } from '@material-ui/styles';
import { Pie, Doughnut, Bar } from 'react-chartjs-2';
import 'chart.js/auto';
import OverlaySpinner from '../OverlaySpinner';

const useStyles = makeStyles(() => ({
  coloredBox: {},
  '@media print': {
    coloredBox: {
      '-webkit-print-color-adjust': 'exact',
      printColorAdjust: 'exact',
    },
  },
}));

const DataChart = ({
  title,
  data,
  type = 'pie',
  direction = 'vertical',
  isStacked = false,
  dataLength = 1,
  size = 150,
  height = 300,
  width = 300,
  loading = false,
  skeleHeight,
  skeleWidth,
  skeleMargin,
  skelePadding,
  spacing = true,
}) => {
  const theme = useTheme();
  const classes = useStyles();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));

  const COLORS = [
    '#172A4A',
    '#FEBF00',
    theme.palette.accent.green,
    theme.palette.accent.darkgrey,
    '#B1D2F5',
  ];

  const OPTIONS = {
    maintainAspectRatio: false,
    responsive: true,
    indexAxis: direction === 'horizontal' ? 'y' : 'x',
    rotation: 0,
    events: type === 'pie' ? [] : ['mousemove'],
    plugins: {
      legend: {
        display: false,
      },
      tooltip: {
        enabled: type === 'donut' || type === 'bar',
        callbacks:
          type === 'donut'
            ? {
                label: (tooltipItem) => {
                  return tooltipItem.parsed;
                },
              }
            : {},
      },
    },
    scales:
      direction === 'horizontal' && type === 'bar'
        ? {
            y: {
              ticks: {
                mirror: true,
                z: 1,
                color: 'black',
                font: {
                  size: 12,
                  weight: 'bold',
                },
              },
            },
            x: {
              ticks: {
                precision: 0,
              },
            },
          }
        : {},
  };

  const DATA = {
    labels: data.map((x) => x.label),
    datasets: [
      {
        backgroundColor:
          type === 'bar'
            ? direction === 'horizontal'
              ? COLORS[4]
              : COLORS[0]
            : COLORS,
        borderColor: COLORS,
        data: data.map((x) => x.value),
      },
    ],
  };

  const stackedDATA = {
    labels: data.map((x) => x.label),
    datasets: [
      {
        label: 'Unmanaged',
        backgroundColor: COLORS[1],
        data: data.map((x) => x.value),
      },
      {
        label: 'Managed',
        backgroundColor: COLORS[0],
        data: data.map((x) => x.stackedValue),
      },
    ],
  };

  const stackedOPTIONS = {
    maintainAspectRatio: false,
    responsive: true,
    scales: {
      y: {
        stacked: true,
      },
      x: {
        stacked: true,
      },
    },
  };

  return (
    <Box position="relative" height="100%">
      {loading && (
        <OverlaySpinner
          roundedCorners
          height={skeleHeight}
          width={skeleWidth}
          m={skeleMargin}
          p={skelePadding}
        />
      )}
      {!loading && type === 'bar' ? (
        isStacked ? (
          <Bar
            data={stackedDATA}
            options={stackedOPTIONS}
            height={height}
            width={width}
          />
        ) : (
          <Bar
            data={DATA}
            options={OPTIONS}
            height={direction === 'horizontal' ? height * dataLength : height}
            width={width}
          />
        )
      ) : (
        <Grid container alignItems="flex-start" justifyContent="center">
          <Grid item>
            {type === 'pie' ? (
              <Pie data={DATA} options={OPTIONS} height={size} width={size} />
            ) : (
              <Doughnut
                data={DATA}
                options={OPTIONS}
                height={size}
                width={size}
              />
            )}
          </Grid>
          <Grid item>
            {!isDesktop && <Box sx={{ pt: 2 }} />}
            {!loading && (
              <Typography
                variant="body1"
                color="textSecondary"
                sx={!spacing ? { mb: 1 } : { mb: 5 }}
              >
                {title}
              </Typography>
            )}
            {data.map((x, i) => (
              <Box key={`${x.label}-${i}`} display="flex" alignItems="center">
                <Box
                  mr={2}
                  width={20}
                  height={20}
                  bgcolor={COLORS[i]}
                  className={classes.coloredBox}
                />
                {typeof x.label === 'string' ? (
                  <Typography sx={{ fontWeight: 600 }} color="textSecondary">
                    {x.label}
                  </Typography>
                ) : (
                  x.label
                )}
              </Box>
            ))}
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

DataChart.propTypes = {
  title: PropTypes.string,
  data: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]).isRequired,
      value: PropTypes.number.isRequired,
      stackedValue: PropTypes.number,
    })
  ).isRequired,
  type: PropTypes.oneOf(['pie', 'donut', 'bar']),
  direction: PropTypes.oneOf(['horizontal', 'vertical']),
  isStacked: PropTypes.bool,
  dataLength: PropTypes.number,
  size: PropTypes.number,
  height: PropTypes.number,
  width: PropTypes.number,
  loading: PropTypes.bool,
  skeleHeight: PropTypes.string,
  skeleWidth: PropTypes.string,
  skeleMargin: PropTypes.number,
  skelePadding: PropTypes.number,
  spacing: PropTypes.bool,
};

export default DataChart;
